import React from 'react';

import {
  TableCell,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Helpers } from 'dsilo-ui-components';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import Chip from '@material-ui/core/Chip';
import MoreVertIcon from '@material-ui/icons/MoreVert';

const useStyles = makeStyles((theme) => ({
  // Generic table cell text
  cell: {
    fontSize: 14,
    fontWeight: 400,
    fontStyle: 'normal',
    color: 'rgba(33,33,33,1)',
    padding: 5,
    [theme.breakpoints.down('sm')]: {
      fontSize: 13,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
    },
  },

  // Assigned To layout
  assignedWrap: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 5,
    fontSize: 14,
    fontWeight: 400,
    fontStyle: 'normal',
    color: 'rgba(33,33,33,1)',
    [theme.breakpoints.down('sm')]: {
      fontSize: 13,
      gap: 4,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
      gap: 4,
    },
  },
  assignedIcon: {
    color: 'rgba(48,164,97,1)',
    height: 30,
    width: 30,
    [theme.breakpoints.down('sm')]: {
      height: 26,
      width: 26,
    },
    [theme.breakpoints.down('xs')]: {
      height: 22,
      width: 22,
    },
  },

  // Action cell
  actionsCell: {
    padding: 5,
  },
  moreIcon: {
    height: 16,
    width: 16,
    color: 'rgba(33,33,33,1)',
    [theme.breakpoints.down('sm')]: {
      height: 15,
      width: 15,
    },
    [theme.breakpoints.down('xs')]: {
      height: 14,
      width: 14,
    },
  },

  // Simple row flex for the menu button
  actionsRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },

  // Action item (icon + text) shown in MenuItem
  actionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  actionItemIcon: {
    width: 18,
    height: 18,
    display: 'block',
  },
  actionItemLabel: {
    fontWeight: 400,
    fontSize: 14,
    color: 'rgba(66,66,66,1)',
    fontStyle: 'normal',
    [theme.breakpoints.down('sm')]: {
      fontSize: 13,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
    },
  },

  // For cells that render a Chip (status, etc.)
  chipCell: {
    fontSize: 14,
    fontWeight: 400,
    fontStyle: 'normal',
    color: 'rgba(33,33,33,1)',
    padding: 5,
    [theme.breakpoints.down('sm')]: {
      fontSize: 13,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
    },
  },
}));

const ActionItem = ({ icon, label, classes }) => (
  <div className={classes.actionItem}>
    <img src={icon} alt={label} className={classes.actionItemIcon} />
    <Typography component="span" className={classes.actionItemLabel}>
      {label}
    </Typography>
  </div>
);

const RenderRowData = (props) => {
  const {
    classess, // external styles passed from parent (chips/menu)
    enableActions,
    tableHeaders,
    row,
    rIndex,
    anchorElDrop,
    handleClick,
    handleClose,
    tableActions,
    onTableActionClick,
  } = props;

  const classes = useStyles();

  return (
    <>
      {tableHeaders?.map((header, index) => {
        const colField = header.name || header.value;
        let colValue = Helpers.generateValueByDataElementType(row, header) || '-';

        // Normalize values for namepicker/select
        if (['namepicker', 'select'].includes(header.type)) {
          colValue =
            typeof colValue === 'object' && colValue !== null
              ? colValue.name || '-'
              : typeof colValue === 'string'
              ? colValue || '-'
              : '-';
        }

        // Assigned To special renderer
        if (header.label === 'Assigned To') {
          return (
            <TableCell key={index} align="left" className={classes.cell}>
              <div className={classes.assignedWrap}>
                <AccountCircleIcon className={classes.assignedIcon} />
                {colValue}
              </div>
            </TableCell>
          );
        }

        // Chip-enabled cells
        if (header.chipEnable) {
          if (colValue === 'Not Yet Started') {
            return (
              <TableCell key={index} align="left" className={classes.chipCell}>
                <Tooltip title={colValue} arrow>
                  <Chip label={colValue} variant="outlined" classes={{ outlined: classess.customChip }} />
                </Tooltip>
              </TableCell>
            );
          }
          if (colValue === 'In Progress') {
            return (
              <TableCell key={index} align="left" className={classes.chipCell}>
                <Tooltip title={colValue} arrow>
                  <Chip label={colValue} variant="outlined" classes={{ outlined: classess.customChip1 }} />
                </Tooltip>
              </TableCell>
            );
          }
          if (colValue === 'Complete') {
            return (
              <TableCell key={index} align="left" className={classes.chipCell}>
                <Tooltip title={colValue} arrow>
                  <Chip label={colValue} variant="outlined" classes={{ outlined: classess.customChip2 }} />
                </Tooltip>
              </TableCell>
            );
          }
        }

        // Default cell
        return (
          <Tooltip key={index} title={colValue} arrow>
            <TableCell align="left" className={classes.cell}>
              {colValue}
            </TableCell>
          </Tooltip>
        );
      })}

      {enableActions && (
        <TableCell key={`actions-${rIndex}`} align="left" className={classes.actionsCell}>
          <div className={classes.actionsRow}>
            <IconButton
              aria-controls={`more-menu-${rIndex}`}
              aria-haspopup="true"
              onClick={(event) => handleClick(event, rIndex)}
            >
              <MoreVertIcon className={classes.moreIcon} />
            </IconButton>

            <Menu
              id={`more-menu-${rIndex}`}
              anchorEl={anchorElDrop}
              classes={{ paper: classess.menuPaper }}
              keepMounted
              open={Boolean(anchorElDrop)}
              onClose={handleClose}
            >
              {tableActions.view && row?.tableActions?.view && (
                <MenuItem
                  onClick={() => onTableActionClick('view', { ...row, onlyViewIt: true })}
                  className={classess.menuItem}
                >
                  <ActionItem icon="/media/svg/viewTask.svg" label="View" classes={classes} />
                </MenuItem>
              )}
              {tableActions.edit && row?.tableActions?.edit && (
                <MenuItem onClick={() => onTableActionClick('edit', row)} className={classess.menuItem}>
                  <ActionItem icon="/media/svg/editTask.svg" label="Edit" classes={classes} />
                </MenuItem>
              )}
              {tableActions.assignMe && row?.tableActions?.assignMe && (
                <MenuItem onClick={() => onTableActionClick('assignToMe', row)} className={classess.menuItem}>
                  <ActionItem icon="/media/svg/assignTo.svg" label="Assign to" classes={classes} />
                </MenuItem>
              )}
              {tableActions.forwardTo && row?.tableActions?.forwardTo && (
                <MenuItem onClick={() => onTableActionClick('forwardTo', row)} className={classess.menuItem}>
                  <ActionItem icon="/media/svg/forwardTo.svg" label="Forward to" classes={classes} />
                </MenuItem>
              )}
              {tableActions.delete && row?.tableActions?.delete && (
                <MenuItem onClick={() => onTableActionClick('deleteTask', row)} className={classess.menuItem}>
                  <ActionItem icon="/media/svg/deleteTask.svg" label="Delete" classes={classes} />
                </MenuItem>
              )}
            </Menu>
          </div>
        </TableCell>
      )}
    </>
  );
};

export default RenderRowData;
